import React from 'react';
import { Swiper } from './ui/Swiper';
import { Edit, Trash2, ExternalLink } from 'lucide-react';
import { ActionMenu } from './ui/ActionMenu';
import type { Release } from '../types/database';

interface ReleasesSliderProps {
  releases: Release[];
  isMember: boolean;
  onEdit: (release: Release) => void;
  onDelete: (id: string) => void;
}

export function ReleasesSlider({ releases, isMember, onEdit, onDelete }: ReleasesSliderProps) {
  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getStreamingUrl = (release: Release) => {
    return release.streaming_url;
  };

  const handleReleaseClick = (e: React.MouseEvent, release: Release) => {
    const streamingUrl = getStreamingUrl(release);
    if (streamingUrl) {
      e.preventDefault();
      handleExternalLink(streamingUrl);
    }
  };

  return (
    <Swiper slidesPerView={4.2}>
      {releases.map((release) => (
        <div key={release.id} className="relative group">
          <div className="bg-gray-custom/10 hover:bg-gray-custom/20 rounded-lg p-4 transition-colors">
            {isMember && (
              <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                <ActionMenu
                  items={[
                    {
                      label: 'Edit Release',
                      icon: <Edit className="w-4 h-4" />,
                      onClick: () => onEdit(release),
                    },
                    {
                      label: 'Delete Release',
                      icon: <Trash2 className="w-4 h-4" />,
                      onClick: () => onDelete(release.id),
                      className: 'text-red-500 hover:text-red-500',
                    },
                  ]}
                />
              </div>
            )}

            <div 
              className={`aspect-square bg-gray-custom/10 rounded-lg overflow-hidden mb-3 ${
                getStreamingUrl(release) ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
              }`}
              onClick={(e) => handleReleaseClick(e, release)}
            >
              <img
                src={release.image_url}
                alt={release.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="px-2 py-1 bg-primary rounded-full text-xs text-light">
                  {release.type}
                </span>
                {release.release_year && (
                  <span className="text-xs text-light/60">
                    {release.release_year}
                  </span>
                )}
              </div>
              
              <h3 
                className={`font-semibold text-light text-md line-clamp-2 ${
                  getStreamingUrl(release) ? 'cursor-pointer hover:text-primary transition-colors' : ''
                }`}
                onClick={(e) => handleReleaseClick(e, release)}
              >
                {release.title}
              </h3>
            </div>
          </div>
        </div>
      ))}
    </Swiper>
  );
}